import { Injectable } from '@angular/core';
import { IPotentialSavingsGraphDTO } from '@app/modules/potential-save-modal/dto/i-potential-savings-graph.dto';
import { EChartsOption } from 'echarts';
import moment from 'moment/moment';

interface IGraphPotentialSavingsBreakdown {
	expiring: number;
	onDemand: number;
}

@Injectable()
export class RenewalOverTimeService {
	private readonly graphPeriodInDays = 365;
	private totalSavingsAtDate = new Map<string, IGraphPotentialSavingsBreakdown>();
	private readonly expiringCommitmentsSeries = 'Expiring commitments potential savings';
	private readonly linuxOnDemandSeries = 'Linux on-demand potential savings';

	public prepareUpdatedChartOption(
		data: IPotentialSavingsGraphDTO,
		dailySavings: number,
		startDate: string,
	): EChartsOption {
		this.totalSavingsAtDate.clear();
		const uncoveredLinux = [];
		const commitmentsExpiration = [];
		const chartXAxisLabels = [];

		const pointDate = moment(startDate);
		let onDemandCommitmentsCurrent = dailySavings;
		let onDemandCommitmentsAccumulative = 0;
		let expiringCommitmentsAccumulative = 0;
		let expiringCommitmentsCurrent = 0;

		for (let i = 0; i < this.graphPeriodInDays; i++) {
			const pointDateString = pointDate.format('YYYY-MM-DD');
			if (data[pointDateString] > 0) {
				expiringCommitmentsCurrent += data[pointDateString];
			}

			onDemandCommitmentsAccumulative += onDemandCommitmentsCurrent;
			expiringCommitmentsAccumulative += expiringCommitmentsCurrent;

			chartXAxisLabels.push(pointDateString);
			uncoveredLinux.push(Number(onDemandCommitmentsCurrent.toFixed(2)));
			commitmentsExpiration.push(Number(expiringCommitmentsCurrent.toFixed(2)));

			this.totalSavingsAtDate.set(pointDateString, {
				onDemand: onDemandCommitmentsAccumulative,
				expiring: expiringCommitmentsAccumulative,
			});
			pointDate.add(1, 'day');
		}

		return {
			xAxis: [
				{
					type: 'category',
					boundaryGap: false,
					data: chartXAxisLabels,
				},
			],
			series: [
				{
					name: this.linuxOnDemandSeries,
					type: 'line',
					stack: 'counts',
					smooth: true,
					showSymbol: false,
					data: uncoveredLinux,
					lineStyle: {
						color: '#5C6BC0',
					},
					areaStyle: {
						color: '#9FA8DA',
					},
				},
				{
					name: this.expiringCommitmentsSeries,
					type: 'line',
					stack: 'counts',
					smooth: true,
					showSymbol: false,
					data: commitmentsExpiration,
					lineStyle: {
						color: '#42A5F5',
					},
					areaStyle: {
						color: '#90CAF9',
					},
				},
			],
		};
	}

	public getFormatter(): (params: any[]) => string {
		return (params: any[]): string => {
			const numberWithCommas = (value): string => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
			const tooltipLabel = (value: number): string =>
				'To date: ' + params[0].axisValue + ', Potential savings: $' + numberWithCommas(value.toFixed(2));
			const point = this.totalSavingsAtDate.get(params[0].axisValue);

			if (!!point) {
				let potentialSavingsTotal = 0;
				if (params.some((i) => i.seriesName === this.expiringCommitmentsSeries)) {
					potentialSavingsTotal += point.expiring;
				}
				if (params.some((i) => i.seriesName === this.linuxOnDemandSeries)) {
					potentialSavingsTotal += point.onDemand;
				}

				return tooltipLabel(potentialSavingsTotal);
			}

			return '';
		};
	}
}
